import { PromiseExtended } from 'dexie';
import { HustroDbServiceTablesUnion, HustroDb } from '.';
import { wrapQuery } from './queryWrapper';
import { ServiceData, isDownloadedForAnotherProject } from './service';
import { DataOwner, DEFAULT_DATA_OWNER } from './dataOwner';
import { logDownloadOfAnotherProject, rethrowServiceTableFailure } from 'serviceWorker/helpers/dataLog';

interface DBEntityService<T = ServiceData> {
  /** Whom the collection belongs to; a downloaded collection of a project records the project. */
  owner: DataOwner;
  add: (data: any) => Promise<void>;
  clear: () => Promise<void>;
  addSync: (data: any) => Promise<void>;
  /** Marks the download as stopped, keeping the rest of the state (`isDownloaded`, `syncKey`). */
  stopDownloading: () => Promise<void>;
  /** For a collection of a project, a download made for another project reads as not downloaded. */
  get: () => Promise<T>;
  reset: () => Promise<void>;
  /** Marks the collection to be downloaded again in full (`shouldReset`) by its next sync. */
  requestDownloadAgain: () => Promise<void>;
}

interface EntityServiceOptions {
  /** `USER` for data valid in every project (projects, organizations). */
  owner?: DataOwner;
}

export function entityServiceFactory<T extends ServiceData>(
  db: HustroDb,
  tableName: HustroDbServiceTablesUnion,
  { owner = DEFAULT_DATA_OWNER }: EntityServiceOptions = {}
): DBEntityService<T> {
  const configuredProjectId = async (): Promise<string | undefined> =>
    (await db.config.toCollection().last())?.projectId;
  const getStored = wrapQuery(db, (): Promise<T> =>
    (db[tableName].toCollection().last() as PromiseExtended<T>).catch(rethrowServiceTableFailure('read', [tableName]))
  );

  const get = wrapQuery(db, async (): Promise<T> => {
    const stored = await getStored();
    if (owner !== DataOwner.PROJECT || !stored?.isDownloaded) {
      return stored;
    }
    const projectId = await configuredProjectId();
    if (!isDownloadedForAnotherProject(stored, projectId)) {
      return stored;
    }
    logDownloadOfAnotherProject(tableName, stored.projectId, projectId);
    return { ...stored, isDownloaded: false, syncKey: undefined };
  });

  const initialData: Omit<ServiceData, '_id'> = {
    isDownloaded: false,
    isDownloading: false,
    total: 0,
    syncKey: undefined,
  };

  /** Replaces the state; runs in the caller's transaction when there is one. */
  const replaceState = (data?: any): Promise<void> =>
    db
      .transaction('rw', db[tableName], async (): Promise<void> => {
        await db[tableName].clear();
        await db[tableName].put(data || initialData);
      })
      .catch(rethrowServiceTableFailure('write', [tableName]));

  /** A finished download of project data: it records the project it was downloaded for. */
  const recordsOwner = (data?: any): boolean => owner === DataOwner.PROJECT && Boolean(data?.isDownloaded);

  /**
   * The state with the configured project, for a finished download of project
   * data; other states as they are. Read before any transaction (the config
   * table is not part of it), and only when needed: `add` and `clear` also run
   * inside a caller's transaction (clearing a table with its service), where
   * waiting for a non-Dexie promise would commit it too early.
   */
  const withOwner = async (data?: any): Promise<any> => ({ ...data, projectId: await configuredProjectId() });

  const add = wrapQuery(db, (data?: any): Promise<void> =>
    recordsOwner(data) ? withOwner(data).then(replaceState) : replaceState(data)
  );

  const clear = wrapQuery(db, (): Promise<void> => replaceState());

  /**
   * A sync changes a downloaded collection and keeps the project it records:
   * only a full download decides whose collection it is. A collection that
   * records none (stored before owners) stays unknown, it is not taken for
   * the configured project's. One transaction: a flush between the read and
   * the replace must not bring the old state back.
   */
  const addSync = wrapQuery(db, (data: any): Promise<void> =>
    db.transaction('rw', db[tableName], async (): Promise<void> => {
      const currentState = await getStored();
      if (!currentState || !currentState.isDownloaded) {
        return;
      }
      await replaceState({ ...data, projectId: currentState.projectId });
    })
  );

  /** Marks a downloaded collection to be downloaded again in full by its next sync. */
  const requestDownloadAgain = wrapQuery(db, (): Promise<void> =>
    db.transaction('rw', db[tableName], async (): Promise<void> => {
      const currentState = await getStored();
      if (currentState) {
        await replaceState({ ...currentState, shouldReset: true });
      }
    })
  );

  // One transaction: a write between the read and the replace (e.g. a new syncKey) is not lost.
  const stopDownloading = wrapQuery(db, (): Promise<void> =>
    db.transaction('rw', db[tableName], async (): Promise<void> => {
      const currentState = await getStored();
      await replaceState({ ...currentState, isDownloading: false });
    })
  );

  const reset = wrapQuery(db, async (): Promise<void> => {
    const data = await getStored();
    if (data?.shouldReset) {
      return replaceState({ ...data, shouldReset: false });
    }
  });

  return {
    owner,
    add,
    clear,
    addSync,
    stopDownloading,
    reset,
    requestDownloadAgain,
    get,
  };
}
